import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {filter, finalize, map, mergeMap, tap} from 'rxjs/operators';
import {ConsoleConfig, OpenSTFConsoleConfig} from '../../../../../webmate/shared/console/interfaces/console-config.interface';
import {makeDevice, OpenSTFDevice} from '../../../../../assets/consoles/openstf/types';
import {EventSocket} from '../../../../../assets/consoles/openstf/transaction/event-socket';
import {of} from 'rxjs/internal/observable/of';
import {keyCodeMapping} from '../../../../../assets/consoles/openstf/keys/mapping';
import { io, Socket } from 'socket.io-client';
import {TransactionResult} from '../../../../../assets/consoles/openstf/transaction/transaction-result';
import _ from 'lodash';
import {normalizeLogMessage} from '../../../../shared/utils/console-util';
import {LogMessage} from '../log-message.interface';
import {AndroidLogLevel, IOSLogLevel} from '../../../../../webmate/shared/console/types/shared';
import {AuthService, AuthStorageStrategy} from '@webmate-web-core/shared/services/auth.service';
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {ConsoleSessionId} from '@webmate-web-core/shared/types/ids';
import {WebSocketSubject} from 'rxjs/internal/observable/dom/WebSocketSubject';

export interface OpenSTFConnection {
   eventSocket: EventSocket;
   connected: boolean;
}

@Injectable()
export class OpenSTFService {

    constructor(private http: HttpClient, private authService: AuthService, private authStorage: AuthStorageStrategy, private webmateOptions: WebmateOptions) {
    }

    /**
     * API
     *
     * Communication with OpenSTF via the OpenSTF-API
     */
    public getDevice(sessionId: ConsoleSessionId, serial: string): Observable<OpenSTFDevice> {
        return this.http.get<{
            device: any
        }>('/console/' + sessionId + '/openstf/api/v1/devices/' + serial).pipe(map(response => {
            return makeDevice(response['device']);
        }));
    }

    public getUserIP(config: ConsoleConfig): Observable<string> {
        return this.http.get<{
            uip: string
        }>('/console/' + config.sessionId + '/openstf/api/v1/user/UIP').pipe(map(response => {
            return response['uip'];
        }));
    }

    public sendInteraction(config: ConsoleConfig, serial: string, commands: string): Observable<boolean> {
        return this.http.get<{
            success: boolean
        }>('/console/' + config.sessionId + `/openstf/api/v1/devices/ios/${serial}/interaction/${encodeURI(commands)}`).pipe(map((response) => {
            return response['success'];
        }));
    }

    /**
     * EVENT SOCKET
     *
     * Communication with OpenSTF via the socket.io event socket that handles interaction with devices and updates
     */
    // SOCKET INITIALIZATION
    private createEventSocket(socketUrl: string, authToken: string): EventSocket {
        const wsUrl: URL = new URL(socketUrl);
        const socket: Socket = io(wsUrl.origin,
            {
                reconnection: true,
                transports: ['websocket'],
                path: wsUrl.pathname,
                query: {
                    access_token: authToken
                }
            }
        );

        return new EventSocket(socket);
    }

    private createLogSocket(socketUrl: string): WebSocket {
        const wsUrl: URL = new URL(socketUrl);
        const hostname: string = wsUrl.port ? wsUrl.host : wsUrl.protocol === 'https:' ? 'wss://' + wsUrl.host : 'ws://' + wsUrl.host;
        return new WebSocket(this.authService.addAuthAsQueryParameter(hostname + '/api' + wsUrl.pathname));
    }


    private filterLog(message: string): boolean {
        return message !== 'ping';
    }

    // TODO decide if this is the right place for parsing the messages or if we should split this up in Raw and Parsed Messages
    private parseLog(message: string, logType: string): LogMessage {
        let parsedResult: RegExpMatchArray | null;

        switch (logType) {
            case 'Android':
                parsedResult = message.match(/^\S*\s\S*\s+(?<pid>\d+)\s+(?<thread_id>\d+)\s(?<loglevel>\w)\s((?<tag>[^:]+):\s)?(?<content>.*)$/);
                if (!!parsedResult) {
                    return {message: parsedResult['groups']!['content'], type: this.parseAndroidLogType(parsedResult['groups']!['loglevel']), tag: parsedResult['groups']!['tag']};
                } else {
                    return {message: message};
                }
            case 'iOS':
                parsedResult = message.match(/^\w{3}[\s\d]{3}\s\d\d:\d\d:\d\d\s([^\[\(\s]+\s)?(?<process>[^\s()]+)\(?(?<library>[\w\.]+)?\)?\[(?<pid>\d+)\]\s\<(?<loglevel>\w+)\>:\s(?<content>.*)$/);
                if (parsedResult) {
                    return {message: parsedResult['groups']!['content'], type: this.parseIOSLogType(parsedResult['groups']!['loglevel']), process: parsedResult['groups']!['process']};
                } else {
                    return {message: message};
                }
            default:
                return {message: message};
        }
    }

    private parseAndroidLogType(logLevel: string): AndroidLogLevel {
        switch (logLevel) {
            case 'D':
                return 'DEBUG';
            case 'V':
                return 'VERBOSE';
            case 'I':
                return 'INFO';
            case 'W':
                return 'WARN';
            case 'E':
                return 'ERR';
            case 'F':
                return 'FATAL';
            default:
                console.warn('loglevel not properly handled for display: ' + logLevel);
                return 'INFO';
        }
    }

    private parseIOSLogType(logLevel: string): IOSLogLevel {
        switch (logLevel) {
            case 'Debug':
                return 'DEBUG';
            case 'Notice':
                return 'NOTICE';
            case 'Info':
                return 'INFO';
            case 'Error':
                return 'ERR';
            case 'Fault':
                return 'FAULT';
            default:
                console.warn('loglevel not properly handled for display: ' + logLevel);
                return 'INFO';
        }
    }

    public startLogStream(sessionId: ConsoleSessionId, device: OpenSTFDevice): Observable<LogMessage[]> {
        let logSocket = new WebSocketSubject<string>({
            url: this.authService.addAuthAsQueryParameter(
                this.webmateOptions.versionedApiUri + '/console/' + sessionId + '/openstf/logsocket'
            ),
            deserializer: msg => msg.data
        });

        const MAX_ENTRIES = 20000;
        let logMessagesBuffer: LogMessage[] = [];

        return logSocket.pipe(
            filter(data => this.filterLog(data)),
            mergeMap(data => data.split('\n')),
            map(line => line.trim()),
            filter(line => line.length > 0),
            map(line => normalizeLogMessage(this.parseLog(line, device.platform))),
            tap(msg => {
                logMessagesBuffer = [...logMessagesBuffer, msg];
                if (logMessagesBuffer.length > MAX_ENTRIES) {
                    logMessagesBuffer.shift();
                }
            }),
            map(() => [...logMessagesBuffer]),
            finalize(() => {
                logSocket.unsubscribe();
            })
        )
    }

    public establishEventSocketConnection(config: ConsoleConfig): Observable<OpenSTFConnection> {
        const eventSocket = this.createEventSocket((config as OpenSTFConsoleConfig).eventSocketUrl, this.authStorage.getAuthInfo().access_token!);
        return eventSocket.waitForConnection().pipe(map(connected => ({connected, eventSocket})));
    }

    public subscribeToDeviceEvents(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice): Observable<OpenSTFDevice> {
        return openSTFConnection.eventSocket.subscribeToDeviceEvents().pipe(map((event) => {
            if (device && event?.data && (event.data.serial === device?.serial)) {
                const d = _.merge(device, event.data);
                return makeDevice(d);
            }
            return device;
        }));
    }

    public sendEvent(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, action: string, data: any): void {
        openSTFConnection.eventSocket.emit(action, device!.channel, data);
    }

    public sendEventExpectingResponse(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, action: string, data?: any): Observable<TransactionResult> {
        const tx = openSTFConnection.eventSocket.createSingleTargetTransaction(device);
        openSTFConnection.eventSocket.emit(action, device!.channel, tx.channel, data);
        return tx.observable;
    }


    // KEYBOARD EVENTS
    // printable input
    public sendText(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, value: string) {
        this.sendEvent(openSTFConnection, device, 'input.type', {
            text: value
        });
    }

    // special keys
    private sendKeyEvent(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, type: 'input.keyDown' | 'input.keyUp' | 'input.keyPress', code: string): void {
        if (Object.keys(keyCodeMapping).indexOf(code) === -1) {
            return;
        }

        const key = keyCodeMapping[code];
        if (key === undefined || key === null) {
            console.debug('Key for code ' + code + ' was unhandled, when trying to send ' + type + ' event.');
        } else {
            this.sendEvent(openSTFConnection, device, type, {
                key: key
            });
        }
    }

    public sendKeyDown(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, key: string): void {
        this.sendKeyEvent(openSTFConnection, device, 'input.keyDown', key);
    }

    public sendKeyUp(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, key: string): void {
        this.sendKeyEvent(openSTFConnection, device, 'input.keyUp', key);
    }

    public sendKeyPress(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, key: string): void {
        this.sendKeyEvent(openSTFConnection, device, 'input.keyPress', key);
    }


    // CLIPBOARD
    public paste(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, clipboardContent: string): Observable<boolean> {
        return this.sendEventExpectingResponse(openSTFConnection, device, 'clipboard.paste', {
            text: clipboardContent
        }).pipe(mergeMap(result => {
            return of(result.success);
        }));
    }

    public copy(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice,): Observable<string> {
        return this.sendEventExpectingResponse(openSTFConnection, device, 'clipboard.copy').pipe(mergeMap(result => {
            return result.data;
        }));
    }


    // ROTATION
    public rotate(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, rotation: number, lock = false) {
        this.sendEvent(openSTFConnection, device, 'display.rotate', {
            rotation: rotation,
            lock: lock
        });
    }


    // ANDROID TOUCH EVENTS
    public gestureStart(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, seq: number) {
        this.sendEvent(openSTFConnection, device, 'input.gestureStart', {
            seq: seq
        });
    }

    public gestureStop(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, seq: number) {
        this.sendEvent(openSTFConnection, device, 'input.gestureStop', {
            seq: seq
        });
    }

    public touchDown(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, seq: number, contact: any, x: number, y: number, pressure: number) {
        this.sendEvent(openSTFConnection, device, 'input.touchDown', {
            seq: seq,
            contact: contact,
            x: x,
            y: y,
            pressure: pressure
        });
    }

    public touchMove(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, seq: number, contact: any, x: number, y: number, pressure: number) {
        this.sendEvent(openSTFConnection, device, 'input.touchMove', {
            seq: seq,
            contact: contact,
            x: x,
            y: y,
            pressure: pressure
        });
    }

    public touchUp(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, seq: number, contact: any) {
        this.sendEvent(openSTFConnection, device, 'input.touchUp', {
            seq: seq,
            contact: contact
        });
    }

    public touchCommit(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, seq: number) {
        this.sendEvent(openSTFConnection, device, 'input.touchCommit', {
            seq: seq
        });
    }

    public touchReset(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, seq: number) {
        this.sendEvent(openSTFConnection, device, 'input.touchReset', {
            seq: seq
        });
    }


    // IOS GESTURES
    public sendTapCoordinate(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, x: number, y: number, id: number) {
        this.sendEvent(openSTFConnection, device, 'input.tapCoordinate', {
            x: x,
            y: y,
            id: id
        });
    }

    public sendTapAndHoldCoordinate(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, x: number, y: number, duration: number, id: number) {
        this.sendEvent(openSTFConnection, device, 'input.tapAndHoldCoordinate', {
            x: x,
            y: y,
            duration: duration,
            id: id
        });
    }

    public sendSwipe(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, fromX: number, fromY: number, toX: number, toY: number, id: number) {
        this.sendEvent(openSTFConnection, device, 'input.swipe', {
            fromX: fromX,
            fromY: fromY,
            toX: toX,
            toY: toY,
            id: id
        });
    }

    public sendDrag(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, fromX: number, fromY: number, toX: number, toY: number, duration: number, id: number) {
        this.sendEvent(openSTFConnection, device, 'input.drag', {
            fromX: fromX,
            fromY: fromY,
            toX: toX,
            toY: toY,
            duration: duration,
            id: id
        });
    }

    // META
    public setImageQuality(openSTFConnection: OpenSTFConnection, device: OpenSTFDevice, maximumSize: number, jpegQuality: number) {
        this.sendEvent(openSTFConnection, device, 'display.compress', {
            size: maximumSize,
            quality: jpegQuality
        });
    }

}
